import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  useTenantLogin,
  useChallenge2FA,
  isTwoFactorChallenge,
} from '@/modules/tenant/hooks/useTenantAuth';
import { Input } from '@marine/ui/input';
import { Button } from '@marine/ui/button';
import { Checkbox } from '@marine/ui/checkbox';
import { Alert } from '@marine/ui/alert';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@marine/ui/form';
import { BrandedAuthLayout } from '@/layouts/BrandedAuthLayout';
import { safeRedirectPath } from '@/modules/tenant/utils/safeRedirectPath';
import { assignLocation } from '@/modules/tenant/utils/assignLocation';
import { Eye, EyeOff, Loader2, ShieldCheck } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { getErrorMessage } from '@marine/utils/getErrorMessage';

const tenantLoginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
  remember: z.boolean().optional(),
});

type TenantLoginFormData = z.infer<typeof tenantLoginSchema>;

type LoginStep = 'credentials' | 'challenge';

export function TenantLoginPage() {
  const { mutateAsync: login, isPending: isLoading, error: apiError } = useTenantLogin();
  const {
    mutateAsync: challenge2FA,
    isPending: isChallenging,
    error: challengeError,
  } = useChallenge2FA();
  const navigate = useNavigate();
  const location = useLocation();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [step, setStep] = useState<LoginStep>('credentials');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [challengeValue, setChallengeValue] = useState('');
  const challengeInputRef = useRef<HTMLInputElement>(null);

  // Extract error message
  const error = apiError ? getErrorMessage(apiError, 'Login failed') : null;
  const challengeErrorMessage = challengeError
    ? getErrorMessage(challengeError, 'Invalid verification code')
    : null;

  // Autofocus the challenge input when we enter the challenge step or toggle input type
  useEffect(() => {
    if (step === 'challenge') {
      challengeInputRef.current?.focus();
    }
  }, [step, useRecoveryCode]);

  const form = useForm<TenantLoginFormData>({
    resolver: zodResolver(tenantLoginSchema),
    defaultValues: {
      email: '',
      password: '',
      remember: false,
    },
  });

  const completeLogin = () => {
    const redirect = safeRedirectPath(new URLSearchParams(location.search).get('redirect'));
    if (redirect?.startsWith('/api/')) {
      assignLocation(redirect);
      return;
    }
    const from =
      redirect ||
      (location.state as { from?: { pathname: string } })?.from?.pathname ||
      '/dashboard';
    navigate(from, { replace: true });
  };

  const onSubmit = async (data: TenantLoginFormData) => {
    try {
      const response = await login(data);

      if (isTwoFactorChallenge(response)) {
        setStep('challenge');
        setUseRecoveryCode(false);
        setChallengeValue('');
        return;
      }

      completeLogin();
    } catch {
      // Error handled by react-query state
    }
  };

  const handleChallengeSubmit = async (codeOverride?: string) => {
    const value = (codeOverride ?? challengeValue).trim();
    if (!value) return;

    try {
      await challenge2FA(
        useRecoveryCode ? { recovery_code: value } : { code: value },
      );
      completeLogin();
    } catch {
      // Clear the input on failure so the user can retry
      setChallengeValue('');
    }
  };

  const cancelChallenge = () => {
    setStep('credentials');
    setChallengeValue('');
    setUseRecoveryCode(false);
    // Clear the password — user must re-enter for security
    form.setValue('password', '');
  };

  if (step === 'challenge') {
    return (
      <BrandedAuthLayout variant="dealer">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleChallengeSubmit();
          }}
          className="block w-full space-y-5"
        >
          <div className="text-center space-y-2 pb-3">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <ShieldCheck className="h-6 w-6 text-primary" />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Two-Factor Authentication
            </h1>
            <p className="text-sm text-muted-foreground">
              {useRecoveryCode
                ? 'Enter one of your recovery codes to continue.'
                : 'Enter the 6-digit code from your authenticator app.'}
            </p>
          </div>

          {challengeErrorMessage && (
            <Alert variant="destructive">
              <p className="text-sm">{challengeErrorMessage}</p>
            </Alert>
          )}

          <div className="space-y-2">
            <label
              htmlFor="challenge-code"
              className="text-sm font-medium"
            >
              {useRecoveryCode ? 'Recovery Code' : 'Verification Code'}
            </label>
            <Input
              ref={challengeInputRef}
              id="challenge-code"
              placeholder={useRecoveryCode ? 'XXXXXXXXXX-XXXXXXXXXX' : '000000'}
              inputMode={useRecoveryCode ? 'text' : 'numeric'}
              autoComplete="one-time-code"
              maxLength={useRecoveryCode ? 21 : 6}
              value={challengeValue}
              disabled={isChallenging}
              onChange={(e) => {
                const next = useRecoveryCode
                  ? e.target.value.toUpperCase()
                  : e.target.value.replace(/\D/g, '');
                setChallengeValue(next);

                // Auto-submit when the TOTP input hits 6 digits
                if (!useRecoveryCode && next.length === 6 && !isChallenging) {
                  handleChallengeSubmit(next);
                }
              }}
              className="text-center font-mono tracking-widest"
            />
          </div>

          <Button type="submit" className="w-full" disabled={isChallenging || !challengeValue}>
            {isChallenging ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Verifying...
              </span>
            ) : (
              'Verify and Sign In'
            )}
          </Button>

          <div className="flex flex-col items-center gap-2 text-sm">
            <button
              type="button"
              onClick={() => {
                setUseRecoveryCode((prev) => !prev);
                setChallengeValue('');
              }}
              className="font-medium text-link hover:underline"
              disabled={isChallenging}
            >
              {useRecoveryCode
                ? 'Use authenticator code instead'
                : "Can't access your authenticator? Use a recovery code"}
            </button>
            <button
              type="button"
              onClick={cancelChallenge}
              className="text-muted-foreground hover:underline"
              disabled={isChallenging}
            >
              Back to login
            </button>
          </div>
        </form>
      </BrandedAuthLayout>
    );
  }

  return (
    <BrandedAuthLayout variant="dealer">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="block w-full space-y-5"
        >
          <div className="text-center space-y-1 pb-3">
            <h1 className="text-2xl font-semibold tracking-tight">Dealer Login</h1>
            <p className="text-sm text-muted-foreground">
              Sign in to your dealership account
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <p className="text-sm">{error}</p>
            </Alert>
          )}

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    placeholder="admin@dealer.com"
                    type="email"
                    disabled={isLoading}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <div className="flex justify-between items-center gap-2.5">
                  <FormLabel>Password</FormLabel>
                </div>
                <div className="relative">
                  <Input
                    placeholder="Your password"
                    type={passwordVisible ? 'text' : 'password'}
                    disabled={isLoading}
                    {...field}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setPasswordVisible(!passwordVisible)}
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  >
                    {passwordVisible ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="remember"
            render={({ field }) => (
              <FormItem className="flex flex-col space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormLabel className="text-sm font-normal cursor-pointer">
                      Remember me
                    </FormLabel>
                  </div>
                  <Link
                    to="/forgot-password"
                    className="text-sm font-semibold text-foreground hover:text-link"
                  >
                    Forgot Password?
                  </Link>
                </div>
              </FormItem>
            )}
          />

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Signing in...
              </span>
            ) : (
              'Sign In'
            )}
          </Button>

        </form>
      </Form>
    </BrandedAuthLayout>
  );
}
